import { authApi } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { env } from '@/config/env';

/**
 * Dynamic MEDIS endpoint resolution — the web port of the mobile
 * `EndpointUrlsService`.
 *
 * The auth server's `GET /v2/endpoint-urls?is_mobile=0` returns a signed
 * envelope whose payload is a list of MEDIS host/path overrides, each tagged
 * with the route family it applies to (`api_version`):
 *   - `"v1"` rows → the access-code calls (NewSession, Timetable,
 *     SetAppointment, …) — resolved by `resolve()`, static fallback
 *     `env.medisBaseUrl`;
 *   - `"v2"` rows → the EasyAuth (`X-ZUMO-AUTH`) calls (/v2/GetPatient, …) —
 *     resolved by `resolveV2()`, static fallback `env.medisV2BaseUrl`.
 *
 * The two families never see each other's rows, so the calls that stayed on
 * v1 keep their overrides while the migrated ones get theirs. `is_mobile=0`
 * asks for the web slice — the mobile app asks for its own with `1` — so the
 * backend can re-point individual MEDIS endpoints at runtime (migrations,
 * killswitch) per platform, without a client release.
 *
 * Safe by design: if the fetch fails or the payload is an unexpected shape, the
 * registry stays empty and the resolvers fall back to the static bases — i.e.
 * the current proxy behaviour. So this can only add capability, never break
 * the working flow.
 *
 * NOTE: the mobile app Ed25519-verifies the envelope signature. We skip that
 * here (the data arrives over HTTPS from our own auth server); wiring
 * verification is a follow-up if the threat model requires it.
 */

/** Which MEDIS route family an override row applies to. */
export type MedisApiVersion = 'v1' | 'v2';

interface EndpointEntry {
  apiName: string;
  hostname: string;
  url: string;
  apiVersion: MedisApiVersion;
}

/** Signed envelope returned by `GET /v2/endpoint-urls` (see auth EndpointSigningService). */
interface EndpointEnvelope {
  v: number;
  /** base64(JSON `EndpointPayload`) */
  data: string;
  issued_at: string;
  valid_until: string;
  sig: string;
}

/** The decoded `data` of the envelope. */
interface EndpointPayload {
  platform: 'web' | 'mobile';
  endpoints: WireEndpoint[];
}

/** One override row as the auth server sends it. */
interface WireEndpoint {
  /** Empty string = the default row of its `api_version`. */
  api_name: string;
  hostname: string;
  url: string | null;
  is_custom: boolean;
  api_version: MedisApiVersion;
}

function base64ToJson<T>(b64: string): T {
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}

function joinUrl(hostname: string, path: string): string {
  return `${hostname.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

/**
 * Upper bound on how long a fetched registry is used before re-fetching. The
 * envelope is signed for about a month, but an admin re-pointing a row
 * (migration, killswitch) must reach tabs that stay open for days — not only
 * the next page load. Same order of magnitude as the mobile app's hourly refresh.
 */
const REGISTRY_MAX_TTL_MS = 60 * 60_000;

class EndpointUrlsService {
  /** Keyed by `${apiVersion}|${apiName.toLowerCase()}` — see `key()`. */
  private entries = new Map<string, EndpointEntry>();
  private validUntil = 0;
  private inFlight: Promise<void> | null = null;

  private static key(version: MedisApiVersion, apiName: string): string {
    return `${version}|${apiName.toLowerCase()}`;
  }

  /** Fetch + cache the registry once (until it expires). Never throws. */
  ensureLoaded(): Promise<void> {
    // Dynamic routing disabled → never hit /v2/endpoint-urls; the resolvers use the static bases.
    if (!env.medisDynamicEndpoints) return Promise.resolve();
    if (this.validUntil > Date.now()) return Promise.resolve();
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  /** Drop the cache (e.g. on logout). */
  clear(): void {
    this.entries.clear();
    this.validUntil = 0;
  }

  private async load(): Promise<void> {
    try {
      // is_mobile=0 → the web slice of the registry (the SP treats NULL/0 as web).
      const { data } = await authApi.get<EndpointEnvelope>(endpoints.auth.endpointUrls, {
        params: { is_mobile: 0 },
      });
      const payload = base64ToJson<EndpointPayload>(data.data);

      const next = new Map<string, EndpointEntry>();
      for (const e of payload.endpoints) {
        // A row without a hostname can't route anything — same rule as the app.
        if (!e.hostname) continue;
        next.set(EndpointUrlsService.key(e.api_version, e.api_name), {
          apiName: e.api_name,
          hostname: e.hostname,
          url: e.url ?? '',
          apiVersion: e.api_version,
        });
      }

      this.entries = next;
      const until = Date.parse(data.valid_until);
      const maxTtl = Date.now() + REGISTRY_MAX_TTL_MS;
      this.validUntil = Number.isNaN(until) ? maxTtl : Math.min(until, maxTtl);
    } catch {
      // Keep whatever we had; short TTL so we retry soon. The resolvers fall back.
      this.validUntil = Date.now() + 60_000;
    }
  }

  /**
   * Resolve a MEDIS **v1** endpoint's absolute URL (access-code flow), using
   * the `api_version === "v1"` rows only:
   *   1. exact api_name match → host + (custom url | /api + path)
   *   2. default entry (api_name === '') → host + /api + path
   *   3. fallback → static medisBaseUrl + path
   */
  resolve(apiName: string, endpointPath: string): string {
    return this.resolveFor('v1', apiName, endpointPath, env.medisBaseUrl);
  }

  /**
   * Resolve a MEDIS **v2** endpoint's absolute URL (EasyAuth / `X-ZUMO-AUTH`
   * flow), using the `api_version === "v2"` rows only — same rule as
   * `resolve()`, final fallback `env.medisV2BaseUrl`. `endpointPath` already
   * includes the `/v2` segment (e.g. `/v2/GetPatient`).
   */
  resolveV2(apiName: string, endpointPath: string): string {
    return this.resolveFor('v2', apiName, endpointPath, env.medisV2BaseUrl);
  }

  private resolveFor(
    version: MedisApiVersion,
    apiName: string,
    endpointPath: string,
    fallbackBase: string,
  ): string {
    if (!env.medisDynamicEndpoints) return `${fallbackBase}${endpointPath}`;

    const entry = this.entries.get(EndpointUrlsService.key(version, apiName));
    const fallbackEntry = this.entries.get(EndpointUrlsService.key(version, ''));
    const pick = entry ?? fallbackEntry;

    if (pick?.hostname) {
      if (entry?.url) return joinUrl(pick.hostname, entry.url);
      return joinUrl(pick.hostname, `/api${endpointPath}`);
    }
    return `${fallbackBase}${endpointPath}`;
  }
}

export const endpointUrls = new EndpointUrlsService();

/**
 * Keeps dynamically-resolved MEDIS calls same-origin when running in proxy mode.
 *
 * The dynamic registry can route each endpoint to a different absolute host; the
 * browser can't reach those cross-origin (CORS). When `medisBaseUrl` is a
 * relative proxy path (e.g. `/api-medis`), we funnel every absolute MEDIS URL
 * through the generic `/__medis?u=<encoded>` proxy (see vite.config.ts). When
 * `medisBaseUrl` is an absolute URL (the "live"/direct env set, or production),
 * absolute URLs are used as-is — the hosts must then allow CORS.
 *
 * Keyed on the base URL (not dev/prod) so the .env LIVE↔PROXY toggle is
 * consistent: proxy set → everything proxied, live set → everything direct.
 */
const useDevProxy = env.medisBaseUrl.startsWith('/');

export function proxifyMedisUrl(url: string): string {
  if (useDevProxy && /^https?:\/\//i.test(url)) {
    return `/__medis?u=${encodeURIComponent(url)}`;
  }
  return url;
}

/** Map of MEDIS v1 endpoint paths → their `api_name` in the dynamic registry. */
export const MEDIS_API_NAME: Record<string, string> = {
  [endpoints.medis.newSession]: 'newsession',
  [endpoints.medis.getPatient]: 'getpatient',
  [endpoints.medis.getAppointments]: 'getappointments',
  [endpoints.medis.getAccessList]: 'getaccesslist',
  [endpoints.medis.getDocument]: 'download',
  [endpoints.medis.registerPatient]: 'registerpatient',
  [endpoints.medis.timetable]: 'timetable',
  [endpoints.medis.setAppointment]: 'setappointment',
};
